import { Fragment, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Button } from '../ui/Button'
import { Spinner } from '../ui/Spinner'
import { QuickDateRange, currentYearRange } from '../ui/DateField'
import { CloudUpload } from 'lucide-react'

interface FisierExtern {
  cale: string
  nume: string
  marime: number
  modificat: string | null
  furnizor: string
  /** Luna din folder (luna serviciului), `AAAA-LL-01`. */
  data: string | null
  /** Când a fost încărcat în cloud (ISO). Nu are legătură cu data facturii. */
  incarcat: string | null
}

interface Raspuns {
  configurat: boolean
  fisiere: FisierExtern[]
  eroare: string | null
  cititLa?: string
}

/** Mărimea: KB sub 1 MB (facturile au de obicei câteva zeci de KB). */
const mb = (b: number) =>
  b < 1024 * 1024
    ? `${Math.max(1, Math.round(b / 1024))} KB`
    : `${(b / 1024 / 1024).toLocaleString('ro-RO', { maximumFractionDigits: 1 })} MB`

/**
 * Facturile externe din cloud — folderul /Financials/FACTURI EXTERNE din Nextcloud.
 *
 * Etapa 2 din „Facturi furnizori" (Alexandru, 14.10.2026). Deocamdată: linkul de partajare și
 * lista fișierelor. Citirea PDF-urilor (furnizor, număr, sumă) și potrivirea cu EA vin după.
 */
export function FacturiExterneCloud({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient()
  const [link, setLink] = useState('')
  const [schimba, setSchimba] = useState(false)

  const q = useQuery({
    queryKey: ['facturi-externe'],
    queryFn: async () =>
      (await api.get<Raspuns>('/facturi-restante/externe', { timeout: 180_000 })).data,
    staleTime: 10 * 60_000,
  })

  const salveaza = useMutation({
    mutationFn: async () =>
      (await api.put<Raspuns>('/facturi-restante/externe/link', { link }, { timeout: 120_000 }))
        .data,
    onSuccess: (r) => {
      qc.setQueryData(['facturi-externe'], r)
      setLink('')
      setSchimba(false)
      if (r.eroare) toast.error(r.eroare)
      else toast.success(`Link salvat — ${r.fisiere.length} fișiere în folder`)
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error
          ?.message ?? 'Linkul n-a putut fi salvat'
      toast.error(msg)
    },
  })

  const reciteste = useMutation({
    mutationFn: async () =>
      (
        await api.get<Raspuns>('/facturi-restante/externe', {
          params: { reciteste: '1' },
          timeout: 180_000,
        })
      ).data,
    onSuccess: (r) => qc.setQueryData(['facturi-externe'], r),
    onError: () => toast.error('Folderul n-a putut fi recitit'),
  })

  if (q.isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner label="Se citește folderul din cloud…" />
      </div>
    )
  }
  const d = q.data

  const formular = canEdit && (!d?.configurat || schimba) && (
    <div className="space-y-2 rounded-lg border border-b4l-border bg-b4l-bg-soft p-3 text-sm">
      <p>
        Lipește aici linkul de partajare al folderului <strong>/Financials/FACTURI EXTERNE</strong>{' '}
        din Nextcloud (de forma <code>https://nx59150.your-storageshare.de/s/…</code>). Ideal, un
        link doar pentru citire („Read only”). Se salvează criptat.
      </p>
      <div className="flex flex-wrap gap-2">
        {/*
          NU `type="password"`: Chrome a completat aici parola salvată pentru CRM, iar căutarea din
          bara laterală a primit adresa de email (14.10.2026). Text obișnuit, ascuns doar vizual.
        */}
        <input
          type="text"
          name="link-facturi-externe"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="https://…/s/…"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          style={{ WebkitTextSecurity: 'disc' } as React.CSSProperties}
          autoComplete="off"
          className="min-w-[320px] flex-1 rounded border border-b4l-border px-3 py-1.5"
        />
        <Button onClick={() => salveaza.mutate()} disabled={!link.trim() || salveaza.isPending}>
          {salveaza.isPending ? 'Se verifică…' : 'Salvează și verifică'}
        </Button>
        {d?.configurat && (
          <Button variant="secondary" onClick={() => setSchimba(false)}>
            Renunță
          </Button>
        )}
      </div>
    </div>
  )

  if (!d?.configurat) {
    return (
      formular || (
        <p className="py-6 text-center text-sm text-b4l-text-muted">
          Linkul folderului de facturi externe nu e încă pus. Îl poate pune un administrator.
        </p>
      )
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-b4l-text-muted">
          Folderul /Financials/FACTURI EXTERNE — <strong>{d.fisiere.length}</strong> fișiere
        </span>
        {canEdit && !schimba && (
          <Button variant="secondary" className="!text-xs" onClick={() => setSchimba(true)}>
            Schimbă linkul
          </Button>
        )}
        <Button
          variant="secondary"
          className="!text-xs"
          disabled={reciteste.isPending}
          onClick={() => reciteste.mutate()}
          title="Lista se memorează 30 de minute; aici o citești din nou din cloud."
        >
          {reciteste.isPending ? 'Se recitește…' : 'Recitește folderul'}
        </Button>
        {d.cititLa && (
          <span className="text-xs text-b4l-text-dim">
            citit la{' '}
            {new Date(d.cititLa).toLocaleTimeString('ro-RO', {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        )}
        <span className="ml-auto text-xs text-b4l-text-dim">
          Urmează: citirea facturilor (furnizor, număr, sumă) și potrivirea cu EA.
        </span>
      </div>
      {formular}
      {d.eroare && (
        <p className="rounded bg-b4l-red-light p-2 text-sm text-b4l-red-dark">{d.eroare}</p>
      )}
      {d.fisiere.length > 0 && <ListaGrupata fisiere={d.fisiere} />}
    </div>
  )
}

const LUNI = [
  'Ianuarie',
  'Februarie',
  'Martie',
  'Aprilie',
  'Mai',
  'Iunie',
  'Iulie',
  'August',
  'Septembrie',
  'Octombrie',
  'Noiembrie',
  'Decembrie',
]

/** Intervalul „Încărcate recent", ca zile locale. */
function recent(de: Date, pana: Date): { de: string; pana: string } {
  return { de: ziLocala(de.toISOString()), pana: ziLocala(pana.toISOString()) }
}

/** Ziua locală `AAAA-LL-ZZ` a unei date ISO — pentru filtrul după încărcare. */
function ziLocala(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const dataOra = (iso: string) =>
  new Date(iso).toLocaleString('ro-RO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

const luna = (iso: string | null) => {
  if (!iso) return '—'
  const [a, l] = iso.split('-')
  return `${l}.${a}`
}

/** Deschide (în tab nou) sau descarcă o factură; cererea poartă autentificarea CRM. */
async function deschide(cale: string, descarca: boolean) {
  const nou = descarca ? null : window.open('', '_blank')
  try {
    const r = await api.get('/facturi-restante/externe/fisier', {
      params: { cale, ...(descarca ? { descarca: '1' } : {}) },
      responseType: 'blob',
      timeout: 120_000,
    })
    const url = URL.createObjectURL(r.data as Blob)
    if (descarca) {
      const a = document.createElement('a')
      a.href = url
      a.download = cale.split('/').pop() ?? 'factura'
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
    } else if (nou) {
      nou.location.href = url
    }
  } catch {
    nou?.close()
    toast.error('Factura n-a putut fi deschisă')
  }
}

/**
 * Facturile grupate pe furnizor, cu filtrul de dată standard din CRM (după luna din folder —
 * luna serviciului) și capul de tabel fix (Alexandru, 14.10.2026).
 */
function ListaGrupata({ fisiere }: { fisiere: FisierExtern[] }) {
  const [dataDe, setDataDe] = useState(() => currentYearRange().from)
  const [dataPana, setDataPana] = useState(() => currentYearRange().to)
  const [cauta, setCauta] = useState('')
  /*
    Data încărcării în cloud — filtru SEPARAT de perioada facturii (Alexandru, 16.10.2026): o
    factură din iulie urcată pe 30 septembrie e „din iulie" pentru contabilitate și „nouă" pentru
    cine verifică ce s-a încărcat. Implicit: toată perioada, ca să nu ascundă nimic.
  */
  const [incDe, setIncDe] = useState('')
  const [incPana, setIncPana] = useState('')
  const [deschise, setDeschise] = useState<Set<string>>(new Set())

  const grupuri = useMemo(() => {
    const t = cauta.trim().toLowerCase()
    const m = new Map<string, FisierExtern[]>()
    for (const f of fisiere) {
      if (dataDe && (f.data ?? '') < dataDe) continue
      if (dataPana && (f.data ?? '') > dataPana) continue
      const zi = ziLocala(f.incarcat)
      if (incDe && zi < incDe) continue
      if (incPana && zi > incPana) continue
      if (t && !f.furnizor.toLowerCase().includes(t) && !f.nume.toLowerCase().includes(t)) continue
      const arr = m.get(f.furnizor) ?? []
      arr.push(f)
      m.set(f.furnizor, arr)
    }
    return [...m.entries()]
      .map(([furnizor, fs]) => ({
        furnizor,
        fisiere: fs,
        luni: [...new Set(fs.map((f) => f.data).filter(Boolean))].sort() as string[],
        ultimaIncarcare:
          fs
            .map((f) => f.incarcat ?? '')
            .sort()
            .pop() || null,
      }))
      .sort((a, b) => b.fisiere.length - a.fisiere.length)
  }, [fisiere, dataDe, dataPana, cauta, incDe, incPana])

  const total = grupuri.reduce((s, g) => s + g.fisiere.length, 0)

  /*
    Selector de lună (Alexandru, 14.10.2026): „mai simplu decât de ales data început și data
    sfârșit". Lunile sunt cele care au facturi în folder, cele mai noi primele.
  */
  const luni = useMemo(() => {
    const m = new Map<string, number>()
    for (const f of fisiere)
      if (f.data) m.set(f.data.slice(0, 7), (m.get(f.data.slice(0, 7)) ?? 0) + 1)
    return [...m.entries()].sort((a, b) => b[0].localeCompare(a[0]))
  }, [fisiere])
  const ultimaZi = (al: string) => {
    const [a, l] = al.split('-').map(Number)
    return `${al}-${String(new Date(a, l, 0).getDate()).padStart(2, '0')}`
  }
  const lunaAleasa =
    dataDe && dataDe.endsWith('-01') && dataPana === ultimaZi(dataDe.slice(0, 7))
      ? dataDe.slice(0, 7)
      : ''
  const comuta = (f: string) =>
    setDeschise((prev) => {
      const n = new Set(prev)
      if (n.has(f)) n.delete(f)
      else n.add(f)
      return n
    })

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs text-b4l-text-muted">
          Luna facturii
          <select
            value={lunaAleasa}
            onChange={(e) => {
              const v = e.target.value
              if (!v) return
              setDataDe(`${v}-01`)
              setDataPana(ultimaZi(v))
            }}
            className="rounded border border-b4l-border bg-white px-2 py-1.5 text-sm text-b4l-text"
          >
            <option value="">— alege luna —</option>
            {luni.map(([al, n]) => {
              const [a, l] = al.split('-')
              return (
                <option key={al} value={al}>
                  {LUNI[Number(l) - 1]} {a} ({n})
                </option>
              )
            })}
          </select>
        </label>
        <QuickDateRange
          fromValue={dataDe}
          toValue={dataPana}
          onChange={(f, t) => {
            setDataDe(f)
            setDataPana(t)
          }}
        />
        <input
          value={cauta}
          onChange={(e) => setCauta(e.target.value)}
          placeholder="Furnizor sau nume fișier"
          className="min-w-[220px] flex-1 rounded border border-b4l-border px-3 py-1.5 text-sm"
        />
        <span className="text-xs text-b4l-text-muted">
          {grupuri.length} furnizori · {total} fișiere
        </span>
      </div>

      <div className="rounded border border-dashed border-b4l-blue bg-b4l-blue-light/40 p-2">
        <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-b4l-blue-dark">
          <CloudUpload size={14} /> Data încărcării în cloud
          <span className="font-normal text-b4l-text-muted">
            — când a fost urcat fișierul în Nextcloud, nu data facturii
          </span>
        </div>
        {/*
          „Încărcate recent" (Alexandru, 16.10.2026): ultimele 5 zile, până azi — cele urcate de
          curând, de verificat dacă au ajuns în contabilitate.
        */}
        <button
          type="button"
          onClick={() => {
            const azi = new Date()
            const de = new Date(azi)
            de.setDate(de.getDate() - 5)
            const r = recent(de, azi)
            if (incDe === r.de && incPana === r.pana) {
              setIncDe('')
              setIncPana('')
            } else {
              setIncDe(r.de)
              setIncPana(r.pana)
            }
          }}
          className={`mb-2 rounded border px-3 py-1 text-xs font-medium ${
            incDe === recent(new Date(Date.now() - 5 * 86_400_000), new Date()).de && incPana
              ? 'border-b4l-blue bg-b4l-blue text-white'
              : 'border-b4l-blue text-b4l-blue-dark hover:bg-b4l-blue-light'
          }`}
        >
          Încărcate recent (ultimele 5 zile)
        </button>
        <QuickDateRange
          fromValue={incDe}
          toValue={incPana}
          onChange={(f, t) => {
            setIncDe(f)
            setIncPana(t)
          }}
        />
      </div>

      <div className="max-h-[70vh] overflow-auto rounded border border-b4l-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-b4l-text-muted">
              <th className="sticky top-0 z-10 bg-b4l-surface shadow-[inset_0_-1px_0_rgb(0_0_0/0.12)] py-2 pl-3 pr-3 font-medium">
                Furnizor / fișier
              </th>
              <th className="sticky top-0 z-10 bg-b4l-surface shadow-[inset_0_-1px_0_rgb(0_0_0/0.12)] py-2 pr-3 font-medium">
                Luna facturii
              </th>
              <th className="sticky top-0 z-10 bg-b4l-surface shadow-[inset_0_-1px_0_rgb(0_0_0/0.12)] py-2 pr-3 font-medium">
                <span className="inline-flex items-center gap-1">
                  <CloudUpload size={12} /> Încărcat în cloud
                </span>
              </th>
              <th className="sticky top-0 z-10 bg-b4l-surface shadow-[inset_0_-1px_0_rgb(0_0_0/0.12)] py-2 pr-3 text-right font-medium">
                Fișiere / mărime
              </th>
              <th className="sticky top-0 z-10 bg-b4l-surface shadow-[inset_0_-1px_0_rgb(0_0_0/0.12)] py-2 pr-3 font-medium" />
            </tr>
          </thead>
          <tbody>
            {grupuri.map((g) => {
              const deschis = deschise.has(g.furnizor)
              return (
                <Fragment key={g.furnizor}>
                  <tr
                    className="cursor-pointer border-t border-b4l-border bg-b4l-bg-soft hover:bg-b4l-bg"
                    onClick={() => comuta(g.furnizor)}
                  >
                    <td className="py-2 pl-3 pr-3 font-medium">
                      <span className="mr-1 inline-block w-3 text-b4l-text-dim">
                        {deschis ? '▾' : '▸'}
                      </span>
                      {g.furnizor}
                    </td>
                    <td className="py-2 pr-3 text-xs text-b4l-text-muted">
                      {g.luni.length === 0
                        ? '—'
                        : g.luni.length === 1
                          ? luna(g.luni[0])
                          : `${luna(g.luni[0])} – ${luna(g.luni[g.luni.length - 1])}`}
                    </td>
                    <td className="py-2 pr-3 text-xs text-b4l-text-muted">
                      {g.ultimaIncarcare ? `ultimul: ${dataOra(g.ultimaIncarcare)}` : '—'}
                    </td>
                    <td className="py-2 pr-3 text-right text-xs tabular-nums">
                      {g.fisiere.length}
                    </td>
                    <td />
                  </tr>
                  {deschis &&
                    g.fisiere.map((f) => (
                      <tr key={f.cale} className="border-t border-b4l-border">
                        <td className="py-1.5 pl-8 pr-3 text-xs">{f.nume}</td>
                        <td className="py-1.5 pr-3 text-xs">{luna(f.data)}</td>
                        <td className="py-1.5 pr-3 text-xs text-b4l-text-muted">
                          {f.incarcat ? dataOra(f.incarcat) : '—'}
                        </td>
                        <td className="py-1.5 pr-3 text-right text-xs tabular-nums">
                          {mb(f.marime)}
                        </td>
                        <td className="whitespace-nowrap py-1.5 pr-3 text-right">
                          <Button
                            variant="secondary"
                            className="!px-2 !py-0.5 !text-xs"
                            onClick={() => void deschide(f.cale, false)}
                          >
                            Vezi
                          </Button>{' '}
                          <Button
                            variant="secondary"
                            className="!px-2 !py-0.5 !text-xs"
                            onClick={() => void deschide(f.cale, true)}
                          >
                            Descarcă
                          </Button>
                        </td>
                      </tr>
                    ))}
                </Fragment>
              )
            })}
          </tbody>
        </table>
        {grupuri.length === 0 && (
          <p className="py-6 text-center text-sm text-b4l-text-muted">
            Nicio factură pentru filtrele alese.
          </p>
        )}
      </div>
    </div>
  )
}
